<template>
  <Dialog :open="isOpen" @close="close">
    <DialogContent class="max-w-none w-screen h-screen p-0 border-none bg-transparent">
      <div class="fixed inset-0 bg-black/90" />

      <!-- Close button -->
      <Button
        variant="ghost"
        size="icon"
        class="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
        @click="close"
      >
        <Icon name="lucide:x" class="w-6 h-6" />
      </Button>

      <!-- Navigation buttons -->
      <Button
        v-if="currentIndex > 0"
        variant="ghost"
        size="icon"
        class="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-50"
        @click.stop="previousImage"
        @keydown.stop
      >
        <Icon name="lucide:chevron-left" class="w-6 h-6" />
      </Button>
      <Button
        v-if="currentIndex < mediaItems.length - 1"
        variant="ghost"
        size="icon"
        class="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-50"
        @click.stop="nextImage"
        @keydown.stop
      >
        <Icon name="lucide:chevron-right" class="w-6 h-6" />
      </Button>

      <!-- Media display -->
      <div class="relative z-40 flex items-center justify-center p-4 h-full">
        <div class="relative max-h-full max-w-full">
          <TweetMediaItem
            v-if="currentMediaItem"
            :item="currentMediaItem"
            class="rounded-lg overflow-hidden"
            :class="{ 'max-h-[90vh]': currentMediaItem.type === 'image' }"
          />
          <EmbeddedText
            v-if="currentMediaItem?.type === 'image' && bubbleId"
            :bubble-id="bubbleId"
            :image-path="currentMediaItem.src"
          />
        </div>
      </div>

      <!-- Dots indicator -->
      <div
        v-if="mediaItems.length > 1"
        class="absolute bottom-4 left-0 right-0 z-50 flex justify-center items-center gap-2"
      >
        <button
          v-for="(_, index) in mediaItems"
          :key="index"
          class="w-2 h-2 rounded-full transition-all duration-200 focus:outline-none"
          :class="[index === currentIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60']"
          @click="currentIndex = index"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import type { MediaItem } from '@/stores/interfaces'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import EmbeddedText from '@/components/ui/media-gallery/EmbeddedText.vue'
import { useLogger } from '@/composables/useLogger'
import { useKeyboardNavigation } from '@/composables/useKeyboardNavigation'

const props = defineProps<{
  isOpen: boolean
  mediaItems: MediaItem[]
  initialIndex: number
  bubbleId?: string
}>()

const emit = defineEmits<{
  close: []
}>()

const currentIndex = ref(props.initialIndex)
const isNavigatingWithButtons = ref(false)
const logger = useLogger()

// Use the keyboard navigation composable
const { isGalleryOpen } = useKeyboardNavigation()

// Make sure the gallery state is synchronized with this component's open state
watch(
  () => props.isOpen,
  (newValue) => {
    if (!newValue && isGalleryOpen.value) {
      // If the ImageViewer is closed but the global state still thinks it's open,
      // this might be due to the Dialog's built-in close behavior
      emit('close')
    }
  }
)

const currentMediaItem = computed(() => {
  const item = props.mediaItems[currentIndex.value]
  logger.debug('🔍 ImageViewer: Current media item changed', {
    bubbleId: props.bubbleId,
    itemType: item?.type,
    itemSrc: item?.src,
    shouldShowEmbeddedText: !!(item?.type === 'image' && props.bubbleId),
  })
  return item
})

watch(
  () => props.initialIndex,
  (newIndex) => {
    currentIndex.value = newIndex
  }
)

function close() {
  logger.debug('🔍 ImageViewer: Closing viewer', {
    currentIndex: currentIndex.value,
    bubbleId: props.bubbleId,
  })

  // Ensure we're not in a race condition with the Dialog's built-in close behavior
  setTimeout(() => {
    emit('close')
  }, 0)
}

function previousImage() {
  if (currentIndex.value > 0) {
    isNavigatingWithButtons.value = true
    currentIndex.value--
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigatingWithButtons.value = false
    }, 100)
  }
}

function nextImage() {
  if (currentIndex.value < props.mediaItems.length - 1) {
    isNavigatingWithButtons.value = true
    currentIndex.value++
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigatingWithButtons.value = false
    }, 100)
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.isOpen || isNavigatingWithButtons.value) return

  // Prevent handling if the event originated from an input element
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement ||
    event.target instanceof HTMLSelectElement
  ) {
    return
  }

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault()
      event.stopPropagation()
      previousImage()
      break
    case 'ArrowRight':
      event.preventDefault()
      event.stopPropagation()
      nextImage()
      break
    case 'Escape':
      event.preventDefault()
      close()
      break
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

// Watch for when the viewer is opened
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      logger.debug('🔍 ImageViewer: Opening viewer', {
        initialIndex: props.initialIndex,
        bubbleId: props.bubbleId,
        totalItems: props.mediaItems.length,
      })
    }
  }
)
</script>
